import { createPayment } from "@/features/payment/utils.ts";
import {
  BackButton,
  Footer,
  Header,
  Page,
  PageContent,
} from "@/components/page.tsx";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { useMachine } from "@/hooks/use-machine.ts";
import { useMutation } from "@tanstack/react-query";
import { getShopFromLocalStorage } from "@/hooks/use-shop.ts";
import { z } from "zod";
import { ErrorModal } from "@/lib/api";
import { getActiveCoupon } from "@/hooks/use-coupon";
import { useApplyPayment } from "@/hooks/use-apply-payment";
import { LoadingSpinner } from "@/components/spinner";
import { useMuiModal } from "@/components/modal.tsx";
import posthog from "posthog-js";
import { useRemovePayment } from "@/hooks/use-remove-payment";
import { api } from "@/lib/server";
import { OfflineWarning } from "@/components/offline-warning";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import {
  PaymentMethodChoice,
  PaymentMethodHelpText,
} from "@/features/payment/payment-method-choice";

export const Route = createFileRoute(
  "/payment-selector-screen/$machineId/{-$dryerBundleId}",
)({
  component: RouteComponent,
  params: {
    parse: (params) =>
      z
        .object({
          machineId: z.coerce.number(),
          dryerBundleId: z.coerce.number().optional(),
        })
        .parse(params),
  },
  loader: async ({ params }) => {
    const machineType =
      (sessionStorage.getItem(`machine-type`) as
        | "washing-machines"
        | "drying-machines") ?? null;

    const coupon = getActiveCoupon();

    try {
      const payment = await createPayment(
        params.machineId,
        params.dryerBundleId,
        coupon?.couponKey,
      );

      if (!machineType) {
        posthog.capture("malformed-parameters", {
          shop: getShopFromLocalStorage(),
          machineType,
          payment,
          params,
          coupon,
        });
        throw redirect({
          to: "/machine-type-selector-screen",
        });
      }
      return { machineType, payment, activeCouponCode: coupon?.code ?? null };
    } catch (e) {
      console.error(e);
      throw redirect({
        to: "/start-screen",
        reloadDocument: true,
      });
    }
  },
});

function RouteComponent() {
  useAudioSlot("page.payment-selector");
  const { t } = useTranslation();
  const { machineId } = Route.useParams();
  const { showModal } = useMuiModal();
  const { data: machine } = useMachine(machineId);
  const { machineType, payment, activeCouponCode } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const removePayment = useRemovePayment();
  const applyPayment = useApplyPayment();

  const balancePaymentMuation = useMutation({
    mutationFn: async () => {
      const response = await api.checkBalancePayment({
        kioskPaymentNonceKey: payment.kioskPaymentNonceKey,
      });
      if (response.type === "error") {
        showModal(<ErrorModal errorKey={response.error} />);
        return;
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (!data) {
        console.error("check balance payment failed");
        return;
      }
      sessionStorage.setItem("price", JSON.stringify(data));
      navigate({ href: `/payment-balance-screen` });
    },
  });

  return (
    <Page>
      <OfflineWarning />
      <Header
        backArrowHref={
          <BackButton
            label={t("Vissza")}
            onClick={() => {
              removePayment.mutate({
                key: payment.kioskPaymentNonceKey,
                cause: "cancelled-by-user",
              });
              navigate({
                to: "/machine-type-selector-screen",
              });
            }}
          />
        }
      />
      {applyPayment.isPending && <LoadingSpinner />}
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-8`}>
          {payment.price === 0 ? (
            <Trans>Indíthatod a gépet!</Trans>
          ) : (
            <Trans>Válassz fizetési módot!</Trans>
          )}
        </h2>
      </PageContent>
      <PaymentMethodChoice
        payment={payment}
        machineId={machineId}
        activeCouponCode={activeCouponCode}
        pending={applyPayment.isPending}
        // Re-runs the loader, which re-creates the payment with (or without) the coupon.
        onReloadPayment={() =>
          router.invalidate({
            filter: (match) => match.routeId === Route.id,
          })
        }
        machineSummary={
          <div className={`text-center min-w-64 text-2xl`}>
            <p className={`mb-8`}>
              {machineType === "washing-machines"
                ? t("Mosógép")
                : t("Szárítógép")}
            </p>
            <h2 className={`font-fancy text-5xl`}>{machine?.capacity} Kg</h2>
          </div>
        }
        onChoose={async (method) => {
          if (method === "card") {
            return navigate({ to: "/payment-card-screen" });
          }
          if (method === "balance") {
            return balancePaymentMuation.mutate();
          }
          await applyPayment.mutateAsync({
            payment,
            purchaseType: method === "cash" ? "KIOSK_CASH" : "FULL_DISCOUNT",
          });
          return navigate({
            to:
              method === "cash" ? "/v5-cash-payment-screen" : "/payment-complete",
          });
        }}
      />
      <Footer helpText={<PaymentMethodHelpText />} />
    </Page>
  );
}
